import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ThumbsDownIcon, ThumbsUpIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { FeedbackRequest, FeedbackSummary, TariffLineView } from "../../../../shared/api.ts";
import { Code } from "@/components/common/code.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Tooltip } from "@/components/ui/tooltip.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { errorMessage } from "@/lib/hooks.ts";
import { TariffSearch } from "./code-picker.tsx";

function WrongCodeDialog({
  open,
  onOpenChange,
  onSubmit,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (code: string, reason: string) => void;
  pending: boolean;
}) {
  const { t } = useI18n();
  const [line, setLine] = useState<TariffLineView | null>(null);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  const reset = (next: boolean) => {
    if (!next) {
      setLine(null);
      setReason("");
      setTouched(false);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent closeLabel={t.common.close} className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t.panel.feedbackDialogTitle}</DialogTitle>
          <DialogDescription>{t.panel.feedbackDialogText}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (line) onSubmit(line.code, reason.trim());
          }}
        >
          <div className="flex flex-col gap-2">
            <Label>{t.panel.correctCode}</Label>
            {line ? (
              <div className="flex items-center gap-3 rounded-lg border bg-primary-soft/50 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <Code value={line.code} className="text-[15px] font-semibold" />
                  <p className="truncate text-[12.5px] text-muted-foreground">{line.title}</p>
                </div>
                <Button variant="ghost-muted" size="icon-xs" aria-label={t.common.clear} onClick={() => setLine(null)}>
                  <XIcon />
                </Button>
              </div>
            ) : (
              <div className={touched ? "rounded-lg border border-danger" : "rounded-lg border"}>
                <TariffSearch onPick={setLine} />
              </div>
            )}
            {touched && !line ? <p className="text-[12px] text-danger">{t.panel.codeRequired}</p> : null}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="feedback-reason">
              {t.panel.reasonLabel} <span className="font-normal text-muted-foreground">({t.common.optional})</span>
            </Label>
            <Textarea
              id="feedback-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t.panel.reasonPlaceholder}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => reset(false)}>
              {t.common.cancel}
            </Button>
            <Button type="submit" disabled={pending}>
              {t.panel.sendFeedback}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "Is this code right?" with thumbs up and down. Shows what was sent once feedback exists. */
export function FeedbackSection({
  sessionId,
  feedback,
  readOnly,
}: {
  sessionId: string;
  feedback: FeedbackSummary | null;
  readOnly: boolean;
}) {
  const { t, fmt } = useI18n();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(false);

  const send = useMutation({
    mutationFn: (body: FeedbackRequest) => api.sendFeedback(sessionId, body),
    onSuccess: () => {
      toast.success(t.panel.feedbackThanks);
      setDialog(false);
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (feedback && !editing) {
    return (
      <div className="flex flex-col gap-1.5 rounded-xl border bg-muted/40 px-3.5 py-3 text-[13px]">
        <div className="flex items-center gap-2">
          {feedback.verdict === "correct" ? (
            <ThumbsUpIcon className="size-4 text-success" aria-hidden />
          ) : (
            <ThumbsDownIcon className="size-4 text-danger" aria-hidden />
          )}
          <span className="font-semibold">
            {feedback.verdict === "correct" ? (
              t.panel.feedbackSentCorrect
            ) : (
              <>
                {t.panel.feedbackSentWrong("")}
                <Code value={feedback.proposedCode} className="font-semibold" />
              </>
            )}
          </span>
          <span className="ml-auto shrink-0 text-[11.5px] text-muted-foreground">{fmt.dateShort(feedback.createdAt)}</span>
        </div>
        {feedback.reason ? <p className="text-[12.5px] text-muted-foreground">{feedback.reason}</p> : null}
        {!readOnly ? (
          <Button variant="link" size="xs" className="self-start text-[12px]" onClick={() => setEditing(true)}>
            {t.panel.feedbackAgain}
          </Button>
        ) : null}
      </div>
    );
  }

  if (readOnly) return null;

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border bg-card px-3.5 py-2.5">
      <span className="text-[13px] font-semibold">{t.panel.feedbackTitle}</span>
      <div className="flex gap-1.5">
        <Tooltip content={t.panel.feedbackYes}>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t.panel.feedbackYes}
            disabled={send.isPending}
            onClick={() => send.mutate({ verdict: "correct" })}
            className="hover:border-success/40 hover:bg-success-soft hover:text-success"
          >
            <ThumbsUpIcon />
          </Button>
        </Tooltip>
        <Tooltip content={t.panel.feedbackNo}>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t.panel.feedbackNo}
            disabled={send.isPending}
            onClick={() => setDialog(true)}
            className="hover:border-danger-border hover:bg-danger-soft hover:text-danger"
          >
            <ThumbsDownIcon />
          </Button>
        </Tooltip>
      </div>
      <WrongCodeDialog
        open={dialog}
        onOpenChange={setDialog}
        pending={send.isPending}
        onSubmit={(code, reason) => send.mutate({ verdict: "wrong", proposedCode: code, reason: reason || undefined })}
      />
    </div>
  );
}
